import { animate } from '@play-co/timestep-core/ui';
import StateObserver from 'src/StateObserver';
import { getPet } from 'src/game/pet';
import { createEmitter } from 'src/lib/Emitter';
import i18n from 'src/lib/i18n/i18n';
import sounds from 'src/lib/sounds';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import { animDuration } from 'src/lib/utils';
import TutorialHand from '../shared/TutorialHand';
import uiConfig from 'src/lib/ui/config';
import features from 'src/replicant/ruleset/features';

export type FooterButtonType =
  | 'slots'
  | 'menu'
  | 'visit'
  | 'friends'
  | 'care'
  | 'game'
  | 'shop'
  | 'support';

type FooterButtonOpts = { type: FooterButtonType };

export class FooterButton extends ButtonScaleViewWithText {
  private static activeButton: FooterButton | undefined;

  private isActive = false;
  private hand: TutorialHand | undefined;

  constructor(protected opts: FooterButtonOpts) {
    const { actionIcons } = uiConfig.footer;
    const { user } = StateObserver.getState();
    const useV2Label = features(user).addFriendsV2;
    const labelKey =
      opts.type === 'friends' && useV2Label
        ? 'footer.friendsV2'
        : `footer.${opts.type}`;

    super({
      ...uiConfig.footer.button,
      icon: actionIcons[opts.type],
      localeText: () => i18n(labelKey),
      tracking: { feature: 'footer', $subFeature: opts.type },
    });

    this.icon?.updateOpts({ scaleMethod: 'contain' });

    // Close menu on lock
    createEmitter(this, ({ ui }) => ui.lockMenu).addListener((lock) => {
      if (lock && this.isActive) this.setActive(false);
    });
  }

  public async onClick() {
    if (this.isActive) {
      this.setActive(false);
      sounds.sfx.play('cancel');
    } else {
      // Menu actions is locked
      if (StateObserver.getState().ui.lockMenu) return;
      // Do not show menu if conversation is not finished
      if (getPet().conversation.inProgress()) return;
      // Another footer menu is already active.
      if (FooterButton.activeButton && FooterButton.activeButton !== this) {
        return;
      }

      this.setActive(true);
      sounds.sfx.play('ok');
    }
  }

  public setActive(isActive: boolean) {
    if (isActive === this.isActive) {
      return;
    }

    if (isActive) {
      if (FooterButton.activeButton && FooterButton.activeButton !== this) {
        return;
      }
      FooterButton.activeButton = this;
    } else if (FooterButton.activeButton === this) {
      FooterButton.activeButton = undefined;
    }

    const { button, buttonActive, actionIcons } = uiConfig.footer;
    const icon = actionIcons[this.opts.type];
    const iconActive = actionIcons[`${this.opts.type}Active`];
    let buttonHeight = button.height;
    let iconHeight = icon.height;
    if (isActive) {
      buttonHeight += 25;
      iconHeight += 25;
      this.updateButtonImage(buttonActive.image);
      this.icon?.setImage(iconActive.image);
    } else {
      this.updateButtonImage(button.image);
      this.icon?.setImage(icon.image);
    }

    animate(this)
      .clear()
      .now(
        {
          height: buttonHeight,
          offsetY: button.height - buttonHeight,
        },
        animDuration,
        animate.easeOut,
      )
      .then(() => {
        if (!this.isActive) {
          // TODO: Do we need that?
          //this.style.zIndex = 0;
        }
      });
    if (this.icon) {
      animate(this.icon)
        .clear()
        .now({ height: iconHeight }, animDuration, animate.easeOut);
    }
    animate(this.label)
      .clear()
      .now(
        { offsetY: iconHeight - icon.height },
        animDuration,
        animate.easeOut,
      );

    this.isActive = isActive;
  }

  /**
   * Show tutorial hand
   */
  public tutorialShow() {
    // Create view not exist
    if (!this.hand) {
      this.hand = new TutorialHand({});
      this.addSubview(this.hand);
    }

    this.hand!.fadeIn(60, 50, false);

    // Do not overlap with others buttons
    this.updateOpts({ zIndex: 5 });
  }

  /**
   * Hide tutorial hand
   */
  public tutorialHide() {
    this.hand?.fadeOut();
  }
}
